'use client';

import React, { useState, useEffect } from 'react';

// ─── FAQ Component ────────────────────────────────────────────────────────────
function FAQItem({ question, answer, index }: {question: string;answer: string;index: number;}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4 hover:text-navy transition-colors"
        aria-expanded={open}
        aria-controls={`faq-answer-${index}`}>
        
        <span className="text-base font-semibold text-brand-fg pr-4">{question}</span>
        <span
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-transform duration-300"
          style={{ background: '#F5F7FB', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
          aria-hidden="true">
          
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 3v8M3 7h8" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div
        id={`faq-answer-${index}`}
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: open ? '400px' : '0px' }}>
        
        <p className="text-brand-body text-sm leading-relaxed pb-5 pr-10">{answer}</p>
      </div>
    </div>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ElectriciansPage() {
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setStickyVisible(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll('.reveal-hidden');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const applyHref = '/apply';
  const phoneHref = 'tel:13237840656';
  const phoneDisplay = '+1 (323) 784-0656';

  const faqItems = [
  {
    question: 'Can I take payment on-site without Wi-Fi?',
    answer:
    "Yes. There are three options that can facilitate that. You can use an app on your phone, a terminal with a SIM card that uses mobile data, or offline processing — so you're never stuck waiting for a connection on a job site."
  },
  {
    question: 'Can I send invoices from the field before collecting payment?',
    answer:
    "Yes. You can create and send a digital invoice straight from the job site. And you're not forced to create an invoice just to make a charge — you can collect payment directly without one if that's what the situation calls for."
  },
  {
    question: 'Do I need new hardware?',
    answer:
    'Not necessarily. You can use your phone with an app and be up and running without any new equipment. That said, it is common to require new hardware — terminals are programmed to the specific bank your merchant account sits at, so existing equipment may not be compatible.'
  },
  {
    question: 'How fast can I get approved?',
    answer:
    "It's possible to get approved same-day as long as all information is correctly provided. We typically say 1–2 days to be safe. The application takes about 5–10 minutes, and once reviewed, your dedicated rep walks you through the configuration."
  },
  {
    question: 'Is there a long-term contract?',
    answer:
    "No long-term contracts. ChargeAct is a month-to-month arrangement. The goal is to earn your business by actually performing better than your current setup — not by locking you in. If it's not working, you're not stuck."
  }];


  const comparisonRows = [
  { label: 'Professionalism', bad: 'None — looks like a side hustle', good: 'Digital invoicing + work order proof' },
  { label: 'Documentation trail', bad: 'A text message', good: 'Invoicing and payment tracking' },
  { label: 'Dispute support', bad: 'No protection at all', good: 'Dedicated rep + dispute support' },
  { label: 'Dedicated rep', bad: 'No human contact', good: 'Named contact, real phone number' },
  { label: 'Pricing transparency', bad: 'Varies / hidden', good: 'Competitive rates — no guessing' }];


  const marginCards = [
  {
    title: 'Tax Compliant Payments and Invoicing',
    description:
    'Set up tax rates to automatically include tax in your invoices and charges — no manual calculations needed. All collected tax is consolidated in one location so you can easily review, report, and stay compliant. Everything syncs to QuickBooks, keeping your books accurate without any extra effort.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>

  },
  {
    title: 'Take offline payments in areas with no signal',
    description:
    'Working in a crawl space, commercial building, or anywhere with spotty signal? ChargeAct lets you capture payments offline and syncs automatically when you\'re back online — so you never lose a sale because of a dead zone.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <line x1="9" y1="9" x2="15" y2="15" />
          <line x1="15" y1="9" x2="9" y2="15" />
        </svg>

  },
  {
    title: 'Appointment Scheduling, Reminders & Reviews',
    description:
    'Give customers a complete experience — automated text reminders keep appointments top of mind, and built-in follow-ups request Google reviews after every job. Sync seamlessly with Google Calendar and Microsoft 365 so your schedule stays organized across every device.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>

  },
  {
    title: 'Keep Repeat Customer Cards on File',
    description:
    'Store cards on file for repeat customers and charge them instantly — no need to collect payment info every visit. Perfect for property managers, commercial accounts, and businesses that need fast, recurring billing without the back-and-forth.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>

  }];


  const solutionCards = [
  {
    title: 'More Ways to Get Paid',
    description:
    'Accept cards, tap-to-pay, digital wallets, ACH bank transfers, and financing options — giving customers the flexibility to pay however they prefer. More payment options means fewer lost jobs and faster collections.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>

  },
  {
    title: 'Streamlined Invoicing & Billing',
    description:
    'Create and send professional invoices from the field in seconds. Customers approve, sign, and pay in one seamless flow — eliminating back-and-forth, cutting admin time, and getting money in your account faster.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>

  },
  {
    title: 'Save Time on Every Job',
    description:
    'Automated reminders, recurring billing, and one-tap checkout eliminate the manual tasks that eat into your day. Spend less time chasing payments and more time on billable work that grows your business.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>

  },
  {
    title: 'Lower Processing Costs',
    description:
    'Competitive rates designed for service businesses mean you keep more of every dollar you earn. Even a small reduction in processing fees adds up to thousands of dollars back in your pocket each year.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>

  },
  {
    title: 'One Dashboard, Full Visibility',
    description:
    'Track every transaction, invoice, and payment in one place. Know exactly where your cash flow stands at any moment — no spreadsheets, no guesswork, and no time wasted hunting down payment records.',
    icon:
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>

  }];


  const steps = [
  {
    number: '01',
    title: 'Apply in minutes',
    description: 'Short application — business info, processing volume, current setup. No long forms, no guesswork. Takes about 5 minutes.',
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>

  },
  {
    number: '02',
    title: 'Your Dedicated Specialist Steps In',
    description: 'A dedicated specialist is assigned to your account, reviews your business, and advises on the right options, equipment, rates, and services for your needs.',
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>

  },
  {
    number: '03',
    title: 'Get your setup',
    description: 'Hardware if needed, mobile app configured, QuickBooks or Clover connected. Your dedicated account manager walks you through the setup — same-day approval is standard.',
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>

  },
  {
    number: '04',
    title: 'Start taking protected payments',
    description: "Invoice from the field, collect on-site, and know that every transaction is documented and every dispute has a rep in your corner. That's it.",
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>

  }];


  const integrations = [
  {
    name: 'Shopify',
    desc: 'Sync invoices and payments automatically',
    logoUrl: "/assets/images/Unknown-1776197655786.png",
    logoBg: '#FFFFFF'
  },
  {
    name: 'WooCommerce',
    desc: 'Open-source eCommerce for WordPress',
    logoUrl: 'https://cdn.worldvectorlogo.com/logos/woocommerce.svg',
    logoBg: '#FFFFFF'
  },
  {
    name: 'BigCommerce',
    desc: 'Enterprise eCommerce platform',
    logoUrl: 'https://cdn.worldvectorlogo.com/logos/bigcommerce-1.svg',
    logoBg: '#FFFFFF'
  },
  {
    name: 'Magento',
    desc: 'Flexible open-source commerce',
    logoUrl: 'https://cdn.worldvectorlogo.com/logos/magento.svg',
    logoBg: '#FFFFFF',
    logoSize: 'max-h-14 max-w-[140px]'
  },
  {
    name: 'QuickBooks',
    desc: 'Sync invoices and payments automatically',
    logoUrl: "/assets/images/Unknown-1-1776197513319.png",
    logoBg: '#FFFFFF'
  },
  {
    name: 'Authorize.net',
    desc: 'Trusted payment gateway since 1996',
    logoUrl: '/assets/images/anetlogo-1776121793762.png',
    logoBg: '#FFFFFF'
  },
  {
    name: 'NMI',
    desc: 'Enterprise gateway for high-volume processing',
    logoUrl: "/assets/images/Unknown-1776121865148.jpeg",
    logoBg: '#FFFFFF'
  },
  {
    name: 'Clover',
    desc: 'POS hardware and mobile payment terminal',
    logoUrl: "/assets/images/Clover_Logo-1776196755912.png",
    logoBg: '#FFFFFF'
  },
  {
    name: 'Recurly',
    desc: 'Subscription billing management',
    logoUrl: "/assets/images/Unknown-2-1776197801987.jpeg",
    logoBg: '#FFFFFF',
    logoSize: 'max-h-12 max-w-[140px]'
  },
  {
    name: 'Bill.com',
    desc: 'AP/AR automation and payments',
    logoUrl: "/assets/images/Unknown-3-1776122244243.png",
    logoBg: '#FFFFFF'
  }];


  const feeMathRows = [
  { job: '$250 service call', avg: '$7.75', chargeact: '$5.60', save: '$2.15' },
  { job: '$950 panel upgrade', avg: '$28.75', chargeact: '$22.20', save: '$6.55' },
  { job: '$3,200 rewire job', avg: '$96.25', chargeact: '$70.72', save: '$25.53' }];


  const ArrowRight = () =>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>;


  const PhoneIcon = () =>
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>;


  const CheckMini = ({ color = '#5B8BFF' }: {color?: string;}) =>
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 6l3 3 5-5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;


  const XMini = () =>
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>;


  const CheckSmall = () =>
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;


  return (
    <>
      <style>{`
        html { scroll-behavior: smooth; }
        body {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background-color: #fff;
          color: #111827;
          -webkit-font-smoothing: antialiased;
        }
        .font-display { font-family: 'Russo One', sans-serif; }
        .noise-overlay::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }
        .glass-card {
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.15);
        }
        .btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #2F6BFF;
          color: #fff;
          font-weight: 700;
          border-radius: 8px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(47,107,255,.35);
        }
        .btn-primary:hover {
          background: #1a58f0;
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(47,107,255,.45);
        }
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          color: #fff;
          border: 1.5px solid rgba(255,255,255,0.5);
          font-weight: 600;
          border-radius: 8px;
          transition: background 0.2s, border-color 0.2s, transform 0.15s;
        }
        .btn-secondary:hover {
          background: rgba(255,255,255,0.1);
          border-color: rgba(255,255,255,0.9);
          transform: translateY(-1px);
        }
        .btn-primary-dark {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #2F6BFF;
          color: #fff;
          font-weight: 700;
          border-radius: 8px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(47,107,255,.3);
        }
        .btn-primary-dark:hover {
          background: #1a58f0;
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(47,107,255,.45);
        }
        .btn-outline-dark {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          color: #2F6BFF;
          border: 1.5px solid #2F6BFF;
          font-weight: 600;
          border-radius: 8px;
          transition: background 0.2s, color 0.2s, transform 0.15s;
        }
        .btn-outline-dark:hover {
          background: #ebf1ff;
          transform: translateY(-1px);
        }
        .solution-card {
          background: #fff;
          border: 1.5px solid #E5E7EB;
          border-radius: 16px;
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s, border-color 0.25s;
        }
        .solution-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(10,61,145,.08);
          border-color: #2F6BFF;
        }
        .comparison-card {
          border-radius: 16px;
          border: 1.5px solid #E5E7EB;
          background: #fff;
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s;
        }
        .comparison-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(10,61,145,.1);
        }
        .comparison-card.featured {
          border-color: #2F6BFF;
          box-shadow: 0 8px 32px rgba(47,107,255,.15);
        }
        .integration-badge {
          background: #fff;
          border: 1.5px solid #E5E7EB;
          border-radius: 12px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .integration-badge:hover {
          border-color: #2F6BFF;
          box-shadow: 0 4px 16px rgba(47,107,255,.1);
        }
        .faq-item { border-bottom: 1px solid #E5E7EB; }
        .shadow-card { box-shadow: 0 4px 24px rgba(10,61,145,.08); }
        .eyebrow {
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #2F6BFF;
        }
        .reveal-hidden {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1);
        }
        .reveal-hidden.revealed {
          opacity: 1;
          transform: translateY(0);
        }
        .stagger-1 { transition-delay: 0.1s; }
        .stagger-2 { transition-delay: 0.2s; }
        .stagger-3 { transition-delay: 0.3s; }
        .stagger-4 { transition-delay: 0.4s; }
        .stagger-5 { transition-delay: 0.5s; }
        .stagger-6 { transition-delay: 0.6s; }
      `}</style>

      {/* ─── STICKY MOBILE CTA ─────────────────────────────────────────────── */}
      <div className={`sticky-cta bg-navy px-4 py-3 flex-col gap-2 shadow-2xl${stickyVisible ? ' visible' : ''}`}>
        <a href={applyHref} className="btn-primary w-full py-3 text-sm text-center font-bold">Apply Now — Get Better Rates</a>
        <a href={phoneHref} className="text-center text-xs text-white/70 font-medium">Or call {phoneDisplay} — talk to a specialist now</a>
      </div>

      {/* ─── NAVBAR ──────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a className="flex items-center gap-2.5 flex-shrink-0" href="/">
            <img src="/assets/images/CHARGEACT_text_logo-1775251213270.png" alt="ChargeAct Logo" className="h-10 w-auto" />
          </a>
          <div className="flex items-center gap-3 sm:gap-4">
            <a href={phoneHref} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-brand-body hover:text-navy transition-colors">
              <PhoneIcon />
              {phoneDisplay}
            </a>
            <a href={applyHref} className="btn-primary-dark px-4 py-2 text-sm hidden sm:inline-flex items-center gap-1.5">Apply Now</a>
            <a href={phoneHref} className="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-accent-pale text-accent" aria-label="Call ChargeAct">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ─── HERO ──────────────────────────────────────────────────────────── */}
        <section
          className="relative min-h-screen flex items-center pt-16 overflow-hidden noise-overlay"
          style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 50%, #2F6BFF 100%)' }}
          aria-label="Hero">
          
          <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.25) 0%, transparent 70%)' }} aria-hidden="true" />
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.15) 0%, transparent 70%)' }} aria-hidden="true" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 w-full relative z-10">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left column */}
              <div className="lg:col-span-6 xl:col-span-5">
                <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full" style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}>
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-accent-light flex-shrink-0" />
                  <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">⚡ For Electrical Contractors</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight tracking-tight mb-6">
Is Your Payment Processing Costing You Thousands?<br />
                  <span className="text-accent-light">It's Time to Stop Overpaying.</span>
                </h1>

                <p className="text-lg sm:text-xl text-white/75 font-normal leading-relaxed mb-8 max-w-xl">
                  Most electrical businesses are overpaying on every transaction, don&apos;t offer credit card payment options, and have no real support when it counts. ChargeAct is built to fix all three.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-8" id="get-started">
                  <a href={applyHref} className="btn-primary px-6 py-3.5 text-base font-bold text-center">See How Much You&apos;re Overpaying</a>
                  <a href={phoneHref} className="btn-secondary px-6 py-3.5 text-base font-semibold text-center">Talk to an Electrical Payments Specialist</a>
                </div>

                <ul className="space-y-2.5">
                  {[
                  'Mobile payments accepted on-site',
                  'Next and Same Day Funding Available',
                  'Transparent Pricing',
                  'Real human USA based support'].
                  map((item) =>
                  <li key={item} className="flex items-center gap-2.5 text-sm text-white/80 font-medium">
                      <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(47,107,255,0.3)' }}>
                        <CheckMini />
                      </span>
                      {item}
                    </li>
                  )}
                </ul>
              </div>

              {/* Right column — hero card */}
              <div className="lg:col-span-6 xl:col-span-7 flex justify-center lg:justify-end">
                <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl">
                  {/* Main invoice card */}
                  <div className="glass-card rounded-2xl p-6 mb-4 shadow-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1">Digital Invoice</p>
                        <p className="text-white font-bold text-lg">Example: Panel Upgrade Job</p>
                        <p className="text-white/60 text-sm">Completed · Documented · Signed</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(47,107,255,0.25)' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <p className="text-white/50 text-xs mb-1">Invoice</p>
                        <p className="font-bold text-sm text-white">$950.00</p>
                      </div>
                      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <p className="text-white/50 text-xs mb-1">Processing Fee</p>
                        <p className="font-bold text-sm text-white">$15.20</p>
                        <p className="text-white/40 text-xs mt-0.5">ChargeAct rate</p>
                      </div>
                      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <p className="text-white/50 text-xs mb-1">You Keep</p>
                        <p className="font-bold text-sm text-green-400">$934.80</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/50">vs. avg competitors: $27.95 fee · You keep $922.05</span>
                      <span className="text-xs font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Save $12.75</span>
                    </div>
                  </div>

                  {/* Stat mini cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="glass-card rounded-xl p-4 float-card-1">
                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400" aria-hidden="true">
                          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                        <span className="text-white/50 text-xs font-medium truncate">Annual Fee Bleed</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-red-400">$6,000+</p>
                      <p className="text-white/40 text-xs mt-1 leading-tight">on leading processing providers at avg volume</p>
                    </div>
                    <div className="glass-card rounded-xl p-4 float-card-2">
                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400" aria-hidden="true">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span className="text-white/50 text-xs font-medium truncate">Book Keeping</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-green-400">Trackable Accounting</p>
                      <span className="inline-block mt-1 text-xs font-bold text-green-400 bg-green-400/15 px-1.5 py-0.5 rounded-full">✓ Compliant</span>
                      <p className="text-white/40 text-xs mt-1 leading-tight">Easy exports for bookkeeping</p>
                    </div>
                    <div className="glass-card rounded-xl p-4 float-card-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-light" aria-hidden="true">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                          <polyline points="17 6 23 6 23 12" />
                        </svg>
                        <span className="text-white/50 text-xs font-medium truncate">Rate Reduced</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-white">Saving $312/mo</p>
                      <p className="text-white/40 text-xs mt-1 leading-tight">vs. flat-rate on same volume</p>
                    </div>
                  </div>

                  {/* Approval badge */}
                  <div className="glass-card rounded-xl p-4 mt-3 flex items-center gap-3 float-card-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.2)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">Account Approved: As Early As Same Day</p>
                      <p className="text-white/50 text-xs">Dedicated rep assigned · Ready to process</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── PROBLEM / COMPARISON ────────────────────────────────────────── */}
        <section id="problem" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">The Payment Stack Problem</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-fg tracking-tight leading-tight mb-5">
                Still Using Venmo, Cash App, or a Basic Flat-Rate Processor?
              </h2>
              <p className="text-lg text-brand-body leading-relaxed">
                Make your electrical business flexible by offering credit cards as a payment option. Emergency repairs and panel upgrades are often unexpected — having payment flexibility can be the difference between landing the job or losing it.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Bad card */}
              <div className="comparison-card reveal-hidden stagger-1">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FEF2F2', color: '#EF4444' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-fg text-base">Venmo / Cash App / Zelle</h3>
                      <p className="text-brand-muted text-xs">Personal payment apps</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    {comparisonRows.map((row) =>
                    <div key={row.label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-brand-muted uppercase tracking-wider">{row.label}</span>
                        <div className="flex items-start gap-1.5">
                          <span className="flex-shrink-0 mt-0.5 text-red-400"><XMini /></span>
                          <span className="text-sm text-brand-body">{row.bad}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl px-4 py-2.5 text-center" style={{ background: '#FEF2F2' }}>
                    <span className="text-sm font-bold text-red-500">Not built for business</span>
                  </div>
                </div>
              </div>

              {/* Good card */}
              <div className="comparison-card featured reveal-hidden stagger-2">
                <div className="bg-accent text-white text-xs font-bold tracking-widest uppercase text-center py-2 rounded-t-2xl">
                  ✓ Recommended for Electricians
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#2F6BFF', color: '#fff' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 9L7.5 4.5L12 9L7.5 13.5L3 9Z" fill="white" fillOpacity="0.65" />
                        <path d="M7.5 9L12 4.5L16.5 9L12 13.5L7.5 9Z" fill="white" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-fg text-base">ChargeAct</h3>
                      <p className="text-brand-muted text-xs">Built for field-service trades</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    {comparisonRows.map((row) =>
                    <div key={row.label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-brand-muted uppercase tracking-wider">{row.label}</span>
                        <div className="flex items-start gap-1.5">
                          <span className="flex-shrink-0 mt-0.5 text-green-500"><CheckSmall /></span>
                          <span className="text-sm text-brand-fg font-medium">{row.good}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl px-4 py-2.5 text-center" style={{ background: '#EBF1FF' }}>
                    <span className="text-sm font-bold text-accent">Built to protect your revenue</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center reveal-hidden">
              <p className="text-brand-body text-base max-w-2xl mx-auto">
                <strong className="text-brand-fg">If a customer doesn&apos;t have the money upfront, don&apos;t risk losing out on the business</strong>{' '}
                by not being flexible on payment options.
              </p>
            </div>
          </div>
        </section>

        {/* ─── MARGINS / FEATURES ──────────────────────────────────────────── */}
        <section id="margins" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">What&apos;s Eating Your Margins</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-fg tracking-tight leading-tight">
                Four places your payment setup is costing you money right now
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {marginCards.map((card, i) =>
              <div
                key={card.title}
                className={`solution-card p-6 reveal-hidden stagger-${i + 1}`}
                style={{ background: '#F5F7FB', border: '1.5px solid #E5E7EB' }}>
                
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 text-accent" style={{ background: '#EBF1FF' }}>
                    {card.icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-base mb-2">{card.title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{card.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ─── FEE MATH ────────────────────────────────────────────────────── */}
        <section id="fee-math" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-12 reveal-hidden">
              <p className="eyebrow mb-3">The Fee Math</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-fg tracking-tight leading-tight mb-4">
                The fee math looks small until you do it on every job.
              </h2>
              <p className="text-brand-body text-base">
                Here&apos;s an example of what a difference in processing could mean for your electrical business with ChargeAct pricing.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-card reveal-hidden" style={{ border: '1.5px solid #E5E7EB' }}>
              <div className="fee-table-header grid grid-cols-4 gap-0">
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90">Job Scenario</div>
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 text-center">Average Processing</div>
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 text-center">ChargeAct</div>
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 text-center">You Save</div>
              </div>
              {feeMathRows.map((row, i) =>
              <div key={row.job} className={`grid grid-cols-4 gap-0 ${i % 2 === 0 ? 'bg-white' : 'bg-brand-secondary'}`}>
                  <div className="px-5 py-4 text-sm font-semibold text-brand-fg">{row.job}</div>
                  <div className="px-5 py-4 text-sm text-center text-red-500 font-semibold line-through decoration-red-300">{row.avg}</div>
                  <div className="px-5 py-4 text-sm text-center text-accent font-bold">{row.chargeact}</div>
                  <div className="px-5 py-4 text-sm text-center font-bold text-green-600">{row.save}</div>
                </div>
              )}
              <div className="grid grid-cols-4 gap-0 bg-accent/5 border-t-2 border-accent/30">
                <div className="px-5 py-4 text-sm font-semibold text-brand-fg font-bold">
                  500 jobs/yr avg $500
                  <span className="block text-xs font-normal text-brand-muted mt-0.5">Annual estimate</span>
                </div>
                <div className="px-5 py-4 text-sm text-center text-red-500 font-semibold line-through decoration-red-300">$7,500/yr</div>
                <div className="px-5 py-4 text-sm text-center text-accent font-bold">$5,500/yr</div>
                <div className="px-5 py-4 text-sm text-center font-bold text-green-600 text-base">$2,000/yr</div>
              </div>
            </div>

            <p className="text-center text-xs text-brand-muted mt-4 leading-relaxed">
              Rates are subject to merchant history and prices vary between businesses. Actual savings vary. For illustrative purposes only.
            </p>

            <div className="text-center mt-8 reveal-hidden">
              <a href={applyHref} className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2">
                Apply for Your Account
                <ArrowRight />
              </a>
            </div>
          </div>
        </section>

        {/* ─── INTEGRATIONS ────────────────────────────────────────────────── */}
        <section id="integrations" className="py-16 lg:py-20" style={{ background: '#F5F7FB', borderTop: '1px solid #E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 reveal-hidden">
              <p className="eyebrow mb-3">Works With Your Stack</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-fg tracking-tight">Connects to the tools you already use</h2>
              <p className="text-brand-body text-base mt-3 max-w-xl mx-auto">
                Use the familiar equipment and technology you already know — ChargeAct integrates with it seamlessly so you never have to start from scratch.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-5">
              {integrations.map((intg, i) =>
              <div key={intg.name} className={`integration-badge flex flex-col items-center gap-3 px-8 py-6 w-full sm:w-48 reveal-hidden stagger-${i + 1}`}>
                  <div className="h-14 flex items-center justify-center">
                    <img
                    src={intg.logoUrl}
                    alt={`${intg.name} logo`}
                    className={`${(intg as any).logoSize ?? 'max-h-10 max-w-[120px]'} w-auto object-contain`}
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }} />
                  
                    <span className="font-display text-navy text-sm font-bold tracking-wide hidden items-center justify-center">{intg.name}</span>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-brand-fg text-sm">{intg.name}</p>
                    <p className="text-brand-muted text-xs mt-0.5">{intg.desc}</p>
                  </div>
                </div>
              )}
              <div className="integration-badge flex flex-col items-center gap-3 px-8 py-6 w-full sm:w-48 reveal-hidden stagger-1">
                <div className="h-10 flex items-center justify-center">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: '#EBF1FF' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
                      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                      <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
                    </svg>
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-bold text-brand-fg text-sm">And More</p>
                  <p className="text-brand-muted text-xs mt-0.5">Ask about your current setup</p>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-brand-muted mt-6">
              Additional integrations available — ask your dedicated rep about your current setup.
            </p>
          </div>
        </section>

        {/* ─── FINAL CTA ───────────────────────────────────────────────────── */}
        <section
          id="final-cta"
          className="py-20 lg:py-28 relative overflow-hidden noise-overlay"
          style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 55%, #2F6BFF 100%)' }}
          aria-label="Final call to action">
          
          <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.2) 0%, transparent 70%)' }} aria-hidden="true" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full" style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}>
              <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">⚡ For Electrical Contractors</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-5">
              Stop losing revenue to weak payment tools.
            </h2>

            <p className="text-lg text-white/75 leading-relaxed mb-10 max-w-2xl mx-auto">
              If your current processor makes disputes harder, delays payment, or skims too much off every job — it&apos;s time to see a better option. Fast conversation, no-pressure review, custom recommendations for your volume.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <a href={applyHref} className="btn-primary px-6 py-3.5 text-base font-bold text-center">See How Much You&apos;re Overpaying</a>
              <a href={phoneHref} className="btn-secondary px-6 py-3.5 text-base font-semibold text-center">Talk to an Electrical Payments Specialist</a>
            </div>

            <div className="flex flex-col items-center gap-3">
              <a href={phoneHref} className="flex items-center gap-2 text-white font-bold text-xl hover:text-accent-light transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                {phoneDisplay}
              </a>
              <div className="flex flex-wrap justify-center gap-6 text-xs text-white/60 font-medium">
                {['Fast conversation', 'No-pressure review', 'Custom recommendations', 'No long-term contract'].map((item) =>
                <span key={item} className="flex items-center gap-1.5">
                    <CheckMini />
                    {item}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FOOTER ──────────────────────────────────────────────────────────── */}
      {/* DISCLAIMER */}
      <section className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold text-gray-600 mb-2">DISCLAIMER 1</p>
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong>IMPORTANT NOTICE — ILLUSTRATIVE DATA ONLY:</strong> The fee comparison figures, savings estimates, processing rate examples, annual cost projections, and all related financial data presented on this page, including but not limited to the fee savings comparison table, the floating savings indicator, and any referenced dollar amounts, are provided solely for illustrative and informational purposes. Such figures constitute hypothetical examples based on generalized assumptions and do not represent actual, guaranteed, or projected results for any specific merchant. Actual processing rates, fees, and savings will vary materially based on individual merchant history, transaction volume, card mix, chargeback history, business type, applicable interchange categories, and other underwriting factors determined at the sole discretion of the acquiring bank and payment network. Nothing contained herein shall constitute a representation, warranty, or guarantee of any specific rate, fee, or savings outcome. Past performance or illustrative examples are not indicative of future results. All rates and terms are subject to change without notice and are contingent upon merchant approval. ChargeAct makes no representations or warranties, express or implied, regarding the accuracy, completeness, or applicability of the illustrative data to any particular merchant&apos;s circumstances.
          </p>
        </div>
      </section>
      <footer className="border-t border-brand-border py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/assets/images/CHARGEACT_text_logo-1775251213270.png" alt="ChargeAct Logo" className="h-8 w-auto" />
            </div>
          </div>
        </div>
      </footer>
    </>);

}